type Listener = (value: unknown) => void

export class SymbolStore {
  private data: Record<string, unknown> = {}
  private listeners = new Map<string, Set<Listener>>()

  /** Classify a variable path into its tier */
  static classifyVariable(path: string): "domain" | "local" | "system" {
    if (path.startsWith("@")) return "local"
    if (path.startsWith("_")) return "system"
    return "domain"
  }

  /** Extract the root namespace from a domain variable path (null for local/system) */
  static extractNamespace(path: string): string | null {
    if (path.startsWith("@") || path.startsWith("_")) return null
    const dotIdx = path.indexOf(".")
    return dotIdx > 0 ? path.slice(0, dotIdx) : path
  }

  set(path: string, value: unknown): void {
    this.setNestedValue(this.data, path.split("."), value)
    // Notify listeners matching this path
    for (const [key, fns] of this.listeners) {
      // Empty key = wildcard listener (subscribe to all changes)
      if (key === "" || path === key || path.startsWith(key + ".") || key.startsWith(path + ".")) {
        const val = key === "" ? undefined : this.get(key)
        fns.forEach((fn) => fn(val))
      }
    }
  }

  /** Set value WITHOUT notifying listeners — prevents computed->setState infinite loops. */
  setQuiet(path: string, value: unknown): void {
    this.setNestedValue(this.data, path.split("."), value)
  }

  get(path: string): unknown {
    return this.getNestedValue(this.data, path.split("."))
  }

  /** Return a shallow clone of the full state tree (for devtools/debugging). */
  getSnapshot(): Record<string, unknown> {
    return { ...this.data }
  }

  resolve(template: string): unknown {
    if (typeof template !== "string") return template

    // Pure symbol reference: "#{path}" → return raw value
    const pureMatch = /^#\{([^}]+)\}$/.exec(template)
    if (pureMatch) {
      const expr = pureMatch[1].trim()
      // Handle negation: #{!path}
      if (expr.startsWith("!")) {
        return !this.get(expr.slice(1))
      }
      // Handle ternary: #{condition ? 'trueVal' : 'falseVal'}
      const ternaryResult = evaluateTernary(expr, (p) => this.resolveValue(p))
      if (ternaryResult !== undefined) return ternaryResult
      // Handle logical OR/AND: #{exprA || exprB} or #{exprA && exprB}
      // Check || first (lower precedence), then &&
      const orIdx = findTopLevelLogical(expr, "||")
      if (orIdx >= 0) {
        const left = this.resolve(`#{${expr.slice(0, orIdx).trim()}}`)
        if (left) return left
        return this.resolve(`#{${expr.slice(orIdx + 2).trim()}}`)
      }
      const andIdx = findTopLevelLogical(expr, "&&")
      if (andIdx >= 0) {
        const left = this.resolve(`#{${expr.slice(0, andIdx).trim()}}`)
        if (!left) return left
        return this.resolve(`#{${expr.slice(andIdx + 2).trim()}}`)
      }
      // Handle comparison: #{path === value} or #{path == value}
      const compMatch = /^(.+?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/.exec(expr)
      if (compMatch) {
        const left = this.resolveValue(compMatch[1].trim())
        const op = compMatch[2]
        const right = this.resolveValue(compMatch[3].trim())
        switch (op) {
          case "===": case "==": return left === right
          case "!==": case "!=": return left !== right
          case ">": return (left as number) > (right as number)
          case "<": return (left as number) < (right as number)
          case ">=": return (left as number) >= (right as number)
          case "<=": return (left as number) <= (right as number)
        }
      }
      return this.get(expr)
    }

    // Pure @local reference: "@{path}" → return raw value
    const localMatch = /^@\{([^}]+)\}$/.exec(template)
    if (localMatch) {
      return this.get(`@${localMatch[1].trim()}`)
    }

    // Mixed: "text #{path} text" → string interpolation
    if (template.includes("#{")) {
      return template.replace(/#\{([^}]+)\}/g, (_, ref) => {
        const expr = (ref as string).trim()
        // Ternary inside template: #{cond ? 'a' : 'b'}
        const tv = evaluateTernary(expr, (p) => this.resolveValue(p))
        if (tv !== undefined) return tv != null ? String(tv) : ""
        const val = this.get(expr)
        return (val != null && typeof val !== "object") ? String(val) : ""
      })
    }

    return template
  }

  subscribe(path: string, listener: Listener): () => void {
    if (!this.listeners.has(path)) {
      this.listeners.set(path, new Set())
    }
    this.listeners.get(path)!.add(listener)
    return () => this.listeners.get(path)?.delete(listener)
  }

  toContext(): Record<string, unknown> {
    return { ...this.data }
  }

  /** Resolve a value expression: string literal, number, boolean, or path lookup. */
  private resolveValue(expr: string): unknown {
    return resolveLiteral(expr, (p) => this.get(p))
  }

  private getNestedValue(obj: unknown, keys: string[]): unknown {
    let current = obj
    for (const key of keys) {
      if (current == null || typeof current !== "object") return undefined
      current = (current as Record<string, unknown>)[key]
    }
    return current
  }

  private setNestedValue(obj: Record<string, unknown>, keys: string[], value: unknown): void {
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      if (!(key in obj) || typeof obj[key] !== "object" || obj[key] === null) {
        obj[key] = {}
      }
      obj = obj[key] as Record<string, unknown>
    }
    obj[keys[keys.length - 1]] = value
  }
}

// ─── Expression evaluation utilities (shared with element-renderer) ──────

/**
 * Evaluate a ternary expression: `condition ? 'trueVal' : 'falseVal'`
 * Branches must be quoted strings. Returns undefined if not a ternary.
 */
export function evaluateTernary(
  expr: string,
  resolve: (path: string) => unknown,
): unknown | undefined {
  // Match: condition ? 'trueVal' : 'falseVal' (single or double quotes)
  // Use non-greedy condition + quoted branches to avoid splitting on : inside Tailwind classes
  const m = /^(.+?)\s*\?\s*(?:'([^']*)'|"([^"]*)")\s*:\s*(?:'([^']*)'|"([^"]*)")$/.exec(expr)
  if (!m) return undefined

  const condition = m[1].trim()
  const trueVal = m[2] ?? m[3]
  const falseVal = m[4] ?? m[5]

  const result = evaluateCondition(condition, resolve)
  return result ? trueVal : falseVal
}

/** Evaluate a condition expression (comparison or truthiness). */
export function evaluateCondition(
  expr: string,
  resolve: (path: string) => unknown,
): boolean {
  // Handle logical OR/AND first (lower precedence than comparison)
  const orIdx = findTopLevelLogical(expr, "||")
  if (orIdx >= 0) {
    return evaluateCondition(expr.slice(0, orIdx).trim(), resolve) ||
           evaluateCondition(expr.slice(orIdx + 2).trim(), resolve)
  }
  const andIdx = findTopLevelLogical(expr, "&&")
  if (andIdx >= 0) {
    return evaluateCondition(expr.slice(0, andIdx).trim(), resolve) &&
           evaluateCondition(expr.slice(andIdx + 2).trim(), resolve)
  }
  const m = /^(.+?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/.exec(expr)
  if (m) {
    const left = resolveLiteral(m[1].trim(), resolve)
    const op = m[2]
    const right = resolveLiteral(m[3].trim(), resolve)
    switch (op) {
      case "===": case "==": return left === right
      case "!==": case "!=": return left !== right
      case ">": return (left as number) > (right as number)
      case "<": return (left as number) < (right as number)
      case ">=": return (left as number) >= (right as number)
      case "<=": return (left as number) <= (right as number)
    }
  }
  // Negation
  if (expr.startsWith("!")) return !resolve(expr.slice(1))
  // Truthiness
  return !!resolve(expr)
}

/**
 * Find top-level logical operator (|| or &&) in an expression,
 * skipping operators inside quotes or parentheses.
 */
function findTopLevelLogical(expr: string, op: string): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = 0; i < expr.length - (op.length - 1); i++) {
    const ch = expr[i]
    if (ch === "'" && !inDouble) inSingle = !inSingle
    else if (ch === '"' && !inSingle) inDouble = !inDouble
    else if (!inSingle && !inDouble) {
      if (ch === "(") depth++
      else if (ch === ")") depth--
      else if (depth === 0 && expr.slice(i, i + op.length) === op) {
        return i
      }
    }
  }
  return -1
}

/** Resolve a literal value (string/number/boolean) or a path. */
export function resolveLiteral(
  expr: string,
  resolve: (path: string) => unknown,
): unknown {
  // String literal: 'value' or "value"
  if (/^'[^']*'$/.test(expr)) return expr.slice(1, -1)
  if (/^"[^"]*"$/.test(expr)) return expr.slice(1, -1)
  // Number literal
  if (/^-?\d+(\.\d+)?$/.test(expr)) return Number(expr)
  // Boolean
  if (expr === "true") return true
  if (expr === "false") return false
  if (expr === "null" || expr === "undefined") return null
  // Path reference
  return resolve(expr)
}
